import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, ArrowRight, Plus, X, Upload, Camera, Image as ImageIcon } from "lucide-react";
import { createMarketplaceProfile, updateMarketplaceProfile, getMarketplaceProfile, ProfileType, PortfolioItem, ProfessionalType } from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { useAppSelector } from "../../../Redux/Hooks";
import { resolveImage } from "../../../Utils/helper";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import MarketplaceLoader from "./MarketplaceLoader";
import { isProfileCompleted } from "./MarketplaceShared";
import {
  PageWrap,
  Container,
  spin,
  LayoutContainer,
  FormColumn,
  SidebarColumn,
  HeaderBlock,
  OnboardingLabel,
  PageStepTitle,
  PageStepDesc,
  StepIndicatorBlock,
  ProgressBars,
  ProgressBar,
  StepText,
  FormContainer,
  FormCard,
  SectionLabel,
  TwoCol,
  ThreeCol,
  FormGroup,
  Label,
  Input,
  Select,
  Textarea,
  PremiumUploadBlock,
  PremiumUploadInner,
  CameraCircle,
  UploadInfoText,
  UploadActionLink,
  PremiumImagePreview,
  PreviewImg,
  PremiumRemoveImgBtn,
  PortfolioList,
  PortfolioItemCard,
  PortLabel,
  PortRemove,
  AddPortRow,
  AddBtn,
  ActionRow,
  ActionLeftGroup,
  BackBtn,
  SkipBtn,
  TopSkipBtn,
  SubmitBtn,
  SidebarCard,
  BulbIconWrap,
  SidebarCardTitle,
  SidebarCardText,
  GradientCard,
  GradientSubTitle,
  GradientTitle,
  AvatarOverlayWrap,
  SmallAvatar,
  MoreBadge,
  PhotoModalOverlay,
  PhotoModalContent,
  ModalTitle,
  OptionButton,
  CloseModalBtn,
  CameraContainer,
  CameraVideo,
  CameraControls,
  CaptureBtn,
} from "./MarketplaceCreateProfilePageStyles";
import styled from "styled-components";

const SpinnerRing = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(201,77,170,0.2);
  border-top-color: #c94daa;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const MarketplaceCreateProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const [formStep, setFormStep] = useState<1 | 2 | 3>(1);
  const [profileType, setProfileType] = useState<ProfileType>("freelancer");
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [profileExistsOnBackend, setProfileExistsOnBackend] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    setLoadingProfile(true);
    getMarketplaceProfile()
      .then(res => {
        const p = res?.data;
        if (p && (p.id || (p as any)._id || p.display_name)) {
          setProfileExistsOnBackend(true);
        }
        if (p && isProfileCompleted(p)) {
          setHasExistingProfile(true);
          setDisplayName(p.display_name || "");
          if (p.contact_phone) {
            setContactPhone(p.contact_phone);
          }
          if (p.profile_type) {
            setProfileType(p.profile_type);
          }
        } else {
          setHasExistingProfile(false);
          if (p?.display_name) setDisplayName(p.display_name);
          if (p?.contact_phone) setContactPhone(p.contact_phone);
          if (user) {
            if (user.role === 5) setProfileType("freelancer");
            else if (user.role === 3 || user.role === 7) setProfileType("studio");
          }
        }
      })
      .catch(() => {
        setProfileExistsOnBackend(false);
        setHasExistingProfile(false);
        if (user) {
          if (user.role === 5) setProfileType("freelancer");
          else if (user.role === 3 || user.role === 7) setProfileType("studio");
        }
      })
      .finally(() => setLoadingProfile(false));
  }, [user]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Basic Information
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("India");
  const [workCategories, setWorkCategories] = useState("");
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [newPortTitle, setNewPortTitle] = useState("");
  const [newPortUrl, setNewPortUrl] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showPhotoSourceModal, setShowPhotoSourceModal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 400, height: 400, facingMode: "user" } });
      setCameraStream(stream);
      setShowCamera(true);
      setShowPhotoSourceModal(false);
    } catch (err) {
      showErrorToast("Could not access camera");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById("webcam-video") as HTMLVideoElement;
    const canvas = document.createElement("canvas");
    if (video) {
      canvas.width = video.videoWidth || 400;
      canvas.height = video.videoHeight || 400;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], "profile_photo.jpg", { type: "image/jpeg" });
            setImageFile(file);
            showSuccessToast("Photo captured!");
          }
          stopCamera();
        }, "image/jpeg", 0.9);
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    showSuccessToast("Photo selected!");
    setShowPhotoSourceModal(false);
  };

  // Freelancer Details
  const [professionalType, setProfessionalType] = useState<ProfessionalType>("photographer");
  const [profession, setProfession] = useState("");
  const [experienceYears, setExperienceYears] = useState("");
  const [skills, setSkills] = useState("");
  const [servicesOfferedFree, setServicesOfferedFree] = useState("");

  // Studio Details
  const [teamSize, setTeamSize] = useState("");
  const [businessInfo, setBusinessInfo] = useState("");
  const [servicesOfferedStudio, setServicesOfferedStudio] = useState("");

  const handleAddPortfolio = () => {
    if (!newPortTitle.trim() || !newPortUrl.trim()) return;
    setPortfolio([...portfolio, { title: newPortTitle.trim(), url: newPortUrl.trim() }]);
    setNewPortTitle("");
    setNewPortUrl("");
  };

  const handleRemovePortfolio = (idx: number) => {
    setPortfolio(portfolio.filter((_, i) => i !== idx));
  };

  const csvToArray = (val: string) =>
    val.split(",").map(s => s.trim()).filter(Boolean);

  const handleSkip = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    localStorage.setItem("marketplace_profile_skipped", "true");
    localStorage.removeItem("marketplace_profile_created");
    localStorage.removeItem("marketplace_profile");
    window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
    sendFirebaseEvent('Marketplace_Profile_Creation_Skipped', 'Marketplace', 'profile_skip', 'all');
    navigate("/marketplace");
  };

  const handleNextFormStep = () => {
    if (formStep === 1) {
      if (!displayName.trim()) {
        showErrorToast("Display name is required");
        return;
      }
      if (contactPhone.trim() && contactPhone.trim().length !== 10) {
        showErrorToast("Contact phone must be exactly 10 digits");
        return;
      }
    }
    setFormStep(prev => (prev + 1) as any);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      showErrorToast("Display name is required");
      return;
    }
    if (contactPhone.trim() && contactPhone.trim().length !== 10) {
      showErrorToast("Contact phone must be exactly 10 digits");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("profile_type", profileType);
      formData.append("role", profileType === "freelancer" ? "5" : "7");
      formData.append("display_name", displayName.trim());
      formData.append("bio", bio.trim());
      formData.append("city", city.trim());
      formData.append("state", state.trim());
      formData.append("country", country.trim());
      formData.append("contact_phone", contactPhone.trim());
      formData.append("contact_email", contactEmail.trim());
      formData.append("is_public", "true");

      csvToArray(workCategories).forEach(cat => {
        formData.append("work_categories", cat);
      });

      formData.append("portfolio", JSON.stringify(portfolio));

      if (profileType === "freelancer") {
        formData.append("professional_type", professionalType);
        formData.append("profession", profession.trim());
        if (experienceYears) formData.append("experience_years", experienceYears);
        csvToArray(skills).forEach(s => formData.append("skills", s));
        csvToArray(servicesOfferedFree).forEach(s => formData.append("services_offered", s));
      } else {
        if (teamSize) formData.append("team_size", teamSize);
        formData.append("business_info", businessInfo.trim());
        csvToArray(servicesOfferedStudio).forEach(s => formData.append("services_offered", s));
      }

      if (imageFile) {
        formData.append("profile_picture", imageFile);
      } else {
        formData.append("profile_picture", profilePicture.trim());
      }

      let profileResData;
      if (profileExistsOnBackend || hasExistingProfile) {
        const res = await updateMarketplaceProfile(formData);
        profileResData = res?.data || res;
        sendFirebaseEvent(hasExistingProfile ? 'Marketplace_Profile_Updated' : 'Marketplace_Profile_Created', 'Marketplace', 'profile_saved', profileType);
        showSuccessToast("Marketplace profile created successfully! 🎉");
      } else {
        try {
          const res = await createMarketplaceProfile(formData);
          profileResData = res?.data || res;
          sendFirebaseEvent('Marketplace_Profile_Created', 'Marketplace', 'profile_created', profileType);
          showSuccessToast("Marketplace profile created successfully! 🎉");
        } catch (createErr: any) {
          const errMessage = (createErr?.response?.data?.message || createErr?.response?.data?.error?.message || "").toLowerCase();
          if (errMessage.includes("already exist")) {
            // Profile entry already present on backend -> update it seamlessly
            const res = await updateMarketplaceProfile(formData);
            profileResData = res?.data || res;
            sendFirebaseEvent('Marketplace_Profile_Created', 'Marketplace', 'profile_created', profileType);
            showSuccessToast("Marketplace profile created successfully! 🎉");
          } else {
            throw createErr;
          }
        }
      }
      localStorage.setItem("marketplace_profile_created", "true");
      localStorage.removeItem("marketplace_profile_skipped");
      const finalProfileData = (profileResData && (profileResData as any).data) ? (profileResData as any).data : profileResData;
      if (finalProfileData) {
        localStorage.setItem("marketplace_profile", JSON.stringify(finalProfileData));
      }
      window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
      navigate("/marketplace");
    } catch (err: any) {
      const msg = err?.response?.data?.message
        || err?.response?.data?.detail
        || "Failed to create profile";
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const getStepTitle = () => {
    switch (formStep) {
      case 1:
        return "The Creative Foundation";
      case 2:
        return "Location & Categories";
      case 3:
        return profileType === "freelancer" ? "Freelancer & Portfolio Details" : "Studio & Portfolio Details";
      default:
        return "";
    }
  };

  const getStepDesc = () => {
    switch (formStep) {
      case 1:
        return "Introduce your professional persona to the global marketplace.";
      case 2:
        return "Specify where you operate and your areas of expertise.";
      case 3:
        return profileType === "freelancer"
          ? "Define your primary roles, experience, services and add portfolio links."
          : "Define your team capacity, business registration, details and add portfolio links.";
      default:
        return "";
    }
  };

  if (loadingProfile) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageWrap>
      <Container>
          <LayoutContainer>
            <FormColumn>
              <HeaderBlock>
                <div>
                  <OnboardingLabel>ONBOARDING</OnboardingLabel>
                  <PageStepTitle>{getStepTitle()}</PageStepTitle>
                  <PageStepDesc>{getStepDesc()}</PageStepDesc>
                </div>
                <StepIndicatorBlock>
                  <TopSkipBtn type="button" onClick={handleSkip} title="Skip profile creation for now and explore marketplace">
                    Skip for now <ArrowRight size={13} />
                  </TopSkipBtn>
                  <ProgressBars>
                    <ProgressBar $active={formStep >= 1} />
                    <ProgressBar $active={formStep >= 2} />
                    <ProgressBar $active={formStep >= 3} />
                  </ProgressBars>
                  <StepText>Step {formStep} of 3</StepText>
                </StepIndicatorBlock>
              </HeaderBlock>

              <FormContainer
                onSubmit={handleSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                    e.preventDefault();
                  }
                }}
              >
                {formStep === 1 && (
                  <FormCard>
                    <SectionLabel>Basic Information</SectionLabel>
                    <TwoCol>
                      <FormGroup>
                        <Label>Display Name *</Label>
                        <Input
                          required
                          placeholder="e.g. Rahul Sharma Photography"
                          value={displayName}
                          onChange={e => setDisplayName(e.target.value)}
                        />
                      </FormGroup>
                    <FormGroup>
                      <Label>Contact Phone</Label>
                      <Input
                        type="tel"
                        maxLength={10}
                        placeholder="e.g. 9876543210"
                        value={contactPhone}
                        onChange={e => setContactPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      />
                    </FormGroup>
                    </TwoCol>

                    <FormGroup>
                      <Label>Bio / Description</Label>
                      <Textarea
                        placeholder="Introduce yourself to potential clients..."
                        rows={4}
                        value={bio}
                        onChange={e => setBio(e.target.value)}
                      />
                    </FormGroup>

                    <TwoCol style={{ alignItems: "center" }}>
                      <FormGroup>
                        <Label>Contact Email</Label>
                        <Input
                          type="email"
                          placeholder="hello@example.com"
                          value={contactEmail}
                          onChange={e => setContactEmail(e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Profile Picture</Label>
                        <PremiumUploadBlock>
                          {imageFile || profilePicture ? (
                            <PremiumImagePreview>
                              <PreviewImg
                                src={imageFile ? URL.createObjectURL(imageFile) : resolveImage(profilePicture)}
                                alt="Profile"
                              />
                              <PremiumRemoveImgBtn
                                type="button"
                                onClick={() => {
                                  setImageFile(null);
                                  setProfilePicture("");
                                }}
                              >
                                <X size={12} />
                              </PremiumRemoveImgBtn>
                            </PremiumImagePreview>
                          ) : (
                            <PremiumUploadInner type="button" onClick={() => setShowPhotoSourceModal(true)}>
                              <CameraCircle>
                                <Camera size={20} />
                              </CameraCircle>
                              <UploadInfoText>
                                <strong>Headshot / Avatar</strong>
                                <span>Upload a high-resolution professional photo. Square format preferred.</span>
                                <UploadActionLink>Choose Photo</UploadActionLink>
                              </UploadInfoText>
                            </PremiumUploadInner>
                          )}
                        </PremiumUploadBlock>
                      </FormGroup>
                    </TwoCol>
                  </FormCard>
                )}

                {formStep === 2 && (
                  <FormCard>
                    <SectionLabel>Location & Categories</SectionLabel>
                    <ThreeCol>
                      <FormGroup>
                        <Label>City</Label>
                        <Input placeholder="Mumbai" value={city} onChange={e => setCity(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>State</Label>
                        <Input placeholder="Maharashtra" value={state} onChange={e => setState(e.target.value)} />
                      </FormGroup>
                      <FormGroup>
                        <Label>Country</Label>
                        <Input placeholder="India" value={country} onChange={e => setCountry(e.target.value)} />
                      </FormGroup>
                    </ThreeCol>
                    <FormGroup>
                      <Label>Work Categories (comma-separated)</Label>
                      <Input
                        placeholder="e.g. wedding, corporate, fashion"
                        value={workCategories}
                        onChange={e => setWorkCategories(e.target.value)}
                      />
                    </FormGroup>
                  </FormCard>
                )}

                {formStep === 3 && (
                  <>
                    {profileType === "freelancer" ? (
                      <FormCard>
                        <SectionLabel>Freelancer Details</SectionLabel>
                        <TwoCol>
                          <FormGroup>
                            <Label>Professional Type</Label>
                            <Select
                              value={professionalType}
                              onChange={e => setProfessionalType(e.target.value as any)}
                            >
                              <option value="photographer">Photographer</option>
                              <option value="videographer">Videographer</option>
                              <option value="both">Both</option>
                            </Select>
                          </FormGroup>
                          <FormGroup>
                            <Label>Profession Name</Label>
                            <Input
                              placeholder="e.g. Wedding Photographer"
                              value={profession}
                              onChange={e => setProfession(e.target.value)}
                            />
                          </FormGroup>
                        </TwoCol>
                        <TwoCol>
                          <FormGroup>
                            <Label>Experience Years</Label>
                            <Input
                              type="number"
                              placeholder="e.g. 5"
                              value={experienceYears}
                              onChange={e => setExperienceYears(e.target.value)}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>Skills (comma-separated)</Label>
                            <Input
                              placeholder="e.g. drone, lightroom, candid"
                              value={skills}
                              onChange={e => setSkills(e.target.value)}
                            />
                          </FormGroup>
                        </TwoCol>
                        <FormGroup>
                          <Label>Services Offered (comma-separated)</Label>
                          <Input
                            placeholder="e.g. pre-wedding, event-coverage"
                            value={servicesOfferedFree}
                            onChange={e => setServicesOfferedFree(e.target.value)}
                          />
                        </FormGroup>
                      </FormCard>
                    ) : (
                      <FormCard>
                        <SectionLabel>Studio Details</SectionLabel>
                        <TwoCol>
                          <FormGroup>
                            <Label>Team Size</Label>
                            <Input
                              type="number"
                              placeholder="e.g. 10"
                              value={teamSize}
                              onChange={e => setTeamSize(e.target.value)}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label>Services Offered (comma-separated)</Label>
                            <Input
                              placeholder="e.g. equipment-rental, video-production"
                              value={servicesOfferedStudio}
                              onChange={e => setServicesOfferedStudio(e.target.value)}
                            />
                          </FormGroup>
                        </TwoCol>
                        <FormGroup>
                          <Label>Business Info</Label>
                          <Input
                            placeholder="e.g. established in 2015, GST registered"
                            value={businessInfo}
                            onChange={e => setBusinessInfo(e.target.value)}
                          />
                        </FormGroup>
                      </FormCard>
                    )}

                    <FormCard>
                      <SectionLabel>Portfolio Links</SectionLabel>
                      {portfolio.length > 0 && (
                        <PortfolioList>
                          {portfolio.map((item, idx) => (
                            <PortfolioItemCard key={idx}>
                              <PortLabel><strong>{item.title}</strong>: {item.url}</PortLabel>
                              <PortRemove onClick={() => handleRemovePortfolio(idx)} type="button">
                                <X size={14} />
                              </PortRemove>
                            </PortfolioItemCard>
                          ))}
                        </PortfolioList>
                      )}
                      <AddPortRow>
                        <Input
                          placeholder="Link Title (e.g. Prewedding Showreel)"
                          value={newPortTitle}
                          onChange={e => setNewPortTitle(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddPortfolio();
                            }
                          }}
                        />
                        <Input
                          placeholder="URL Link (e.g. https://youtube.com/...)"
                          value={newPortUrl}
                          onChange={e => setNewPortUrl(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddPortfolio();
                            }
                          }}
                        />
                        <AddBtn type="button" onClick={handleAddPortfolio}>Add</AddBtn>
                      </AddPortRow>
                    </FormCard>
                  </>
                )}

                <ActionRow>
                  <ActionLeftGroup>
                    {formStep > 1 && (
                      <BackBtn onClick={() => setFormStep(prev => (prev - 1) as any)} type="button">Back</BackBtn>
                    )}
                    <SkipBtn onClick={handleSkip} type="button" title="Skip profile creation for now and explore marketplace">
                      Skip for now
                    </SkipBtn>
                  </ActionLeftGroup>
                  {formStep < 3 ? (
                    <SubmitBtn key="next-btn" onClick={handleNextFormStep} type="button">
                      Next Step <ArrowRight size={16} />
                    </SubmitBtn>
                  ) : (
                    <SubmitBtn key="submit-btn" disabled={submitting} type="submit">
                      {submitting ? "Creating..." : "Create Profile"}
                    </SubmitBtn>
                  )}
                </ActionRow>
              </FormContainer>
            </FormColumn>

            <SidebarColumn>
              <SidebarCard>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <BulbIconWrap>
                    <span style={{ fontSize: "18px" }}>💡</span>
                  </BulbIconWrap>
                  <SidebarCardTitle>Pro Tips</SidebarCardTitle>
                </div>
                <SidebarCardText>
                  Profiles with high-resolution headshots and a detailed bio get 4x more engagement from high-end agencies.
                </SidebarCardText>
              </SidebarCard>

              <GradientCard>
                <GradientSubTitle>GLOBAL REACH</GradientSubTitle>
                <GradientTitle>Your profile will be visible to 15k+ producers monthly.</GradientTitle>
                <AvatarOverlayWrap>
                  <SmallAvatar src="https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?auto=format&fit=crop&q=80&w=80&h=80" style={{ zIndex: 3 }} />
                  <SmallAvatar src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=80&h=80" style={{ zIndex: 2, marginLeft: "-10px" }} />
                  <SmallAvatar src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=80&h=80" style={{ zIndex: 1, marginLeft: "-10px" }} />
                  <MoreBadge>+12</MoreBadge>
                </AvatarOverlayWrap>
              </GradientCard>
            </SidebarColumn>
          </LayoutContainer>
      </Container>

      {/* Choice Modal */}
      {showPhotoSourceModal && (
        <PhotoModalOverlay onClick={() => setShowPhotoSourceModal(false)}>
          <PhotoModalContent onClick={e => e.stopPropagation()}>
            <CloseModalBtn onClick={() => setShowPhotoSourceModal(false)}><X size={18} /></CloseModalBtn>
            <ModalTitle>Select Profile Photo</ModalTitle>
            <OptionButton onClick={startCamera}>
              <Camera size={18} color="#c94daa" /> Take Photo (Camera)
            </OptionButton>
            <OptionButton onClick={() => document.getElementById("gallery-image-upload")?.click()}>
              <ImageIcon size={18} color="#ffc951" /> Choose from Gallery
            </OptionButton>
            <input
              id="gallery-image-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
          </PhotoModalContent>
        </PhotoModalOverlay>
      )}

      {/* Camera Live Modal */}
      {showCamera && (
        <PhotoModalOverlay>
          <PhotoModalContent style={{ maxWidth: "400px" }}>
            <CloseModalBtn onClick={stopCamera}><X size={18} /></CloseModalBtn>
            <ModalTitle>Camera Preview</ModalTitle>
            <CameraContainer>
              <CameraVideo id="webcam-video" autoPlay playsInline ref={el => { if (el && cameraStream) el.srcObject = cameraStream; }} />
            </CameraContainer>
            <CameraControls>
              <CaptureBtn type="button" onClick={capturePhoto}>Capture Photo</CaptureBtn>
              <OptionButton type="button" onClick={stopCamera} style={{ background: "transparent", padding: "10px 20px" }}>Cancel</OptionButton>
            </CameraControls>
          </PhotoModalContent>
        </PhotoModalOverlay>
      )}
    </PageWrap>
  );
};

export default MarketplaceCreateProfilePage;
